/**
 * 单页内容编辑面板（新字段模型优先）
 * 新模型：按 schema 的 FIELDS 渲染——区块分组 + 中文 label + 类型化编辑器（文字/图片/视频/数据）
 * 旧模型（未迁移页面）：资源锚点 + 文案替换 + 通用数据 三块
 */
<template>
  <div class="pp">
    <div class="pp-head">
      <div>
        <h2 class="pp-title">{{ title }}</h2>
        <span class="pp-scope">内容分片：{{ scope }}</span>
      </div>
      <div class="pp-ops">
        <el-button size="small" @click="reload">重新载入</el-button>
        <el-button size="small" type="primary" :loading="saving" @click="save">{{ dirty ? '保存 *' : '保存' }}</el-button>
      </div>
    </div>

    <!-- ===== 新字段模型 ===== -->
    <template v-if="fieldModel">
      <section class="pp-block" v-for="g in fieldModel.groups" :key="g.title">
        <h3 class="pp-h3">{{ g.title }}</h3>
        <div class="pp-fields">
          <div class="pp-field" :class="{ 'pp-field-wide': isWideField(f) }" v-for="f in g.fields" :key="f.key">
            <label class="pp-field-label">{{ f.label }}</label>

            <!-- 单行文字 -->
            <el-input v-if="f.type === 'text'" v-model="model[f.key]" size="small"
              :placeholder="String(f.default)" @input="touch" />

            <!-- 多行文字 -->
            <el-input v-else-if="f.type === 'textarea'" v-model="model[f.key]" type="textarea"
              :rows="3" :placeholder="String(f.default)" @input="touch" />

            <!-- 图片 -->
            <div v-else-if="f.type === 'image'" class="pp-media">
              <div class="pp-media-prev">
                <img v-if="model[f.key]" :src="mediaUrl(model[f.key])" alt="" />
                <span v-else class="pp-media-empty">未设置（使用页面内置）</span>
              </div>
              <div class="pp-media-btns">
                <el-button size="mini" type="primary" @click="pickFromLibrary(f.key, 'image')">素材库选择</el-button>
                <el-button size="mini" @click="pickLocal(f.key, 'image')">本地上传</el-button>
                <el-button size="mini" type="danger" plain :disabled="!model[f.key]" @click="setField(f.key, '')">恢复默认</el-button>
              </div>
            </div>

            <!-- 视频 -->
            <div v-else-if="f.type === 'video'" class="pp-media">
              <div class="pp-media-prev">
                <video v-if="model[f.key]" :src="mediaUrl(model[f.key])" muted></video>
                <span v-else class="pp-media-empty">未设置（使用页面内置）</span>
              </div>
              <div class="pp-media-btns">
                <el-button size="mini" type="primary" @click="pickFromLibrary(f.key, 'video')">素材库选择</el-button>
                <el-button size="mini" @click="pickLocal(f.key, 'video')">本地上传</el-button>
                <el-button size="mini" type="danger" plain :disabled="!model[f.key]" @click="setField(f.key, '')">恢复默认</el-button>
              </div>
            </div>

            <!-- 对象：JSON 编辑 -->
            <el-input v-else-if="f.type === 'object'" type="textarea" :rows="8"
              :value="jsonOf(f.key)" @input="v => setJson(f.key, v)" size="small" />

            <!-- 数组：对象数组表格化，基础类型数组 JSON 编辑 -->
            <template v-else-if="f.type === 'array'">
              <div v-if="isObjArray(model[f.key])">
                <el-table :data="model[f.key]" border size="small" class="pp-table" max-height="420">
                  <el-table-column type="index" width="50" align="center"></el-table-column>
                  <el-table-column v-for="col in columnsOf(model[f.key])" :key="col" :label="colLabel(col, model[f.key])" min-width="160">
                    <template slot-scope="s">
                      <div v-if="isMediaCol(col)" class="pp-cell-media">
                        <el-input v-model="s.row[col]" size="mini" placeholder="留空用内置默认"></el-input>
                        <div class="pp-cell-btns">
                          <el-button size="mini" type="text" @click="pickLocalCell(f.key, s.$index, col)">本地上传</el-button>
                          <el-button size="mini" type="text" @click="pickFromLibraryCell(f.key, s.$index, col)">素材库</el-button>
                          <el-button v-if="s.row[col]" size="mini" type="text" class="pp-cell-clear" @click="setCell(f.key, s.$index, col, '')">清除</el-button>
                        </div>
                      </div>
                      <!-- 嵌套对象数组（如 devices.metrics 运行指标）：逐项数据框编辑 -->
                      <div v-else-if="isNestedArrCell(s.row, col)" class="pp-cell-nested">
                        <span class="pp-cell-nested-sum">{{ nestedSummary(s.row, col) }}</span>
                        <el-button size="mini" type="primary" plain @click="openNested(f.key, s.$index, col)">逐项编辑</el-button>
                      </div>
                      <!-- 嵌套普通对象：JSON 编辑 -->
                      <el-input v-else-if="isJsonCell(s.row, col)" type="textarea" :rows="4"
                        :value="cellJson(f.key, s.$index, col)"
                        @input="v => setCellJson(f.key, s.$index, col, v)" size="mini"></el-input>
                      <!-- 普通单元格：原值是数字则保存回数字，避免 toFixed 等前端逻辑被字符串打断 -->
                      <el-input v-else :value="cellStr(s.row, col)"
                        @input="v => setCellSmart(f.key, s.$index, col, v)" size="mini"></el-input>
                    </template>
                  </el-table-column>
                  <el-table-column label="操作" width="80" align="center">
                    <template slot-scope="s">
                      <el-button size="mini" type="text" @click="delRow(f.key, s.$index)">删除</el-button>
                    </template>
                  </el-table-column>
                </el-table>
                <el-button size="mini" @click="addRow(f.key)" style="margin-top:8px">新增一行</el-button>
              </div>
              <el-input v-else type="textarea" :rows="6" :value="jsonOf(f.key)" @input="v => setJson(f.key, v)" size="small" />
            </template>
          </div>
        </div>
      </section>
    </template>

    <!-- ===== 旧模型（兼容未迁移页面） ===== -->
    <template v-else>
      <!-- 资源 -->
      <section class="pp-block" v-if="anchors.length">
        <h3 class="pp-h3">页面资源<span class="pp-h3-sub">替换后立即在该静态页生效；留空则使用程序内置默认素材</span></h3>
        <div class="pp-assets">
          <div class="pp-asset" v-for="a in anchors" :key="a.key">
            <div class="pp-asset-preview">
              <img v-if="a.accept === 'image' && valueOfAsset(a.key)" :src="valueOfAsset(a.key)" alt="" />
              <video v-else-if="a.accept === 'video' && valueOfAsset(a.key)" :src="valueOfAsset(a.key)" muted></video>
              <span v-else class="pp-asset-empty">未设置<br />当前为内置默认{{ a.accept === 'video' ? '视频' : '图片' }}</span>
            </div>
            <div class="pp-asset-body">
              <p class="pp-asset-label">{{ a.label }}</p>
              <p class="pp-asset-hint">{{ a.hint }}</p>
              <div class="pp-asset-btns">
                <el-button size="mini" type="primary" @click="pickFromLibraryOld(a)">素材库选择</el-button>
                <el-button size="mini" @click="pickLocalOld(a)">本地上传</el-button>
                <el-button size="mini" type="danger" plain @click="clearAsset(a)" :disabled="!valueOfAsset(a.key)">恢复默认</el-button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 文案 -->
      <section class="pp-block">
        <h3 class="pp-h3">页面文案<span class="pp-h3-sub">共 {{ rows.length }} 条 · 原文案由大屏页面运行后自动登记</span></h3>
        <div class="pp-search">
          <el-input v-model="textKeyword" size="small" placeholder="搜索原文案或替换文案" clearable style="width:280px"></el-input>
          <el-button size="small" @click="addRow">新增自定义文案</el-button>
          <span class="pp-tip">「替换为」留空表示保持原文案</span>
        </div>
        <el-table :data="pagedRows" border size="small" class="pp-table" max-height="420">
          <el-table-column label="原文案（页面上当前出现的文字）" min-width="240">
            <template slot-scope="s"><el-input v-model="s.row.key" size="mini" placeholder="原文案"></el-input></template>
          </el-table-column>
          <el-table-column label="替换为" min-width="240">
            <template slot-scope="s"><el-input v-model="s.row.value" size="mini" placeholder="留空则保持原文案"></el-input></template>
          </el-table-column>
          <el-table-column label="操作" width="90" align="center">
            <template slot-scope="s"><el-button size="mini" type="text" @click="removeRow(s.$index)">删除</el-button></template>
          </el-table-column>
        </el-table>
      </section>

      <!-- 结构化数据 -->
      <section class="pp-block">
        <h3 class="pp-h3">结构化数据<span class="pp-h3-sub">对象数组可表格化编辑，其余按 JSON 编辑</span></h3>
        <div class="pp-data-empty" v-if="!rootKeys.length">该页面尚未登记结构化数据。页面接入 $C(scope, 'key', 默认值) 后即可在此维护。</div>
        <el-collapse v-else v-model="openKeys">
          <el-collapse-item v-for="k in rootKeys" :key="k" :name="k">
            <template slot="title"><span class="pp-key">{{ k }}</span><span class="pp-key-type">{{ typeLabel(dataOf(k)) }}</span></template>
            <div v-if="isObjArray(dataOf(k))">
              <el-table :data="dataOf(k)" border size="small" class="pp-table">
                <el-table-column type="index" width="50" align="center"></el-table-column>
                <el-table-column v-for="col in columnsOf(dataOf(k))" :key="col" :label="col" min-width="160">
                  <template slot-scope="s"><el-input v-model="s.row[col]" size="mini"></el-input></template>
                </el-table-column>
                <el-table-column label="操作" width="80" align="center">
                  <template slot-scope="s"><el-button size="mini" type="text" @click="delDataRow(k, s.$index)">删除</el-button></template>
                </el-table-column>
              </el-table>
              <el-button size="mini" @click="addDataRow(k)" style="margin-top:8px">新增一行</el-button>
            </div>
            <div v-else><el-input type="textarea" :rows="6" :value="jsonOfOld(k)" @input="v => setJsonOld(k, v)" size="small"></el-input></div>
          </el-collapse-item>
        </el-collapse>
      </section>
    </template>

    <!-- 嵌套对象数组逐项编辑（如设备运行指标） -->
    <el-dialog :visible.sync="nestedVisible" :title="nestedTitle" width="900px" append-to-body>
      <el-table :data="nestedRows" border size="small" max-height="420">
        <el-table-column type="index" width="50" align="center"></el-table-column>
        <el-table-column v-for="c in nestedCols" :key="c.key" :label="c.label" min-width="110">
          <template slot-scope="s">
            <el-input :value="cellStr(s.row, c.key)" @input="v => setNestedCell(s.$index, c.key, v)" size="mini"></el-input>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="70" align="center">
          <template slot-scope="s">
            <el-button size="mini" type="text" class="pp-cell-clear" @click="delNestedRow(s.$index)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-button size="mini" @click="addNestedRow" style="margin-top:8px">新增一项</el-button>
      <p class="pp-nested-tip">修改后点外层「保存」生效；数值列保持数字类型，供前端小数位/波动逻辑使用。</p>
    </el-dialog>

    <el-dialog :visible.sync="libVisible" title="从素材库选择" width="900px" append-to-body>
      <div class="pp-lib">
        <div class="pp-lib-item" v-for="m in mediaItems" :key="m.url" @click="chooseMedia(m)">
          <img v-if="m.type === 'image'" :src="m.url" alt="" />
          <video v-else-if="m.type === 'video'" :src="m.url" muted></video>
          <span v-else class="pp-lib-file">{{ m.name }}</span>
          <p class="pp-lib-name">{{ m.name }}</p>
        </div>
        <p v-if="!mediaItems.length" class="pp-lib-empty">素材库为空，请先到「素材库」上传</p>
      </div>
    </el-dialog>

    <input ref="file" type="file" :accept="fileAccept" hidden @change="onFilePicked" />
  </div>
</template>

<script>
import Vue from 'vue'
import api from '@/cms/api'
import store, { state, load, save, replace } from '@/cms/store'
import { anchorsOf, pageTitle, fieldsModel } from '@/cms/schema'
import { mediaUrl } from '@/cms/config'

export default {
  name: 'CmsPagePanel',
  props: { scope: { type: String, required: true } },
  data() {
    return {
      saving: false,
      model: {},             // 新模型：{ key: value } 编辑副本（初始空对象，避免异步加载前模板访问 model[key] 报错）
      // 旧模型
      rows: [],
      textKeyword: '',
      page: 1,
      pageSize: 20,
      openKeys: [],
      // 媒体选择
      libVisible: false,
      mediaItems: [],
      currentTarget: null,    // 正在编辑的媒体目标：{kind:'field',key,accept} 或 {kind:'cell',key,index,col,accept}
      currentAnchor: null,     // 旧模型：资源锚点
      fileAccept: 'image/*',
      // 嵌套对象数组编辑（如 devices.metrics）
      nestedVisible: false,
      nestedTarget: null,      // {key,index,col}
    }
  },
  computed: {
    title() { return pageTitle(this.scope) },
    fieldModel() { return fieldsModel(this.scope) },
    anchors() { return anchorsOf(this.scope) },
    dirty() { return !!state.dirty[this.scope] },
    bundle() { return state.bundle[this.scope] || null },
    rawTexts() { return (this.bundle && this.bundle.texts) || {} },
    // 旧模型数据
    dataObj() { return (this.bundle && this.bundle.fields) || {} },
    rootKeys() { return Object.keys(this.dataObj) },
    // 嵌套编辑弹窗
    nestedRows() {
      const t = this.nestedTarget
      if (!t || !this.model) return []
      const cell = this.model[t.key] && this.model[t.key][t.index] && this.model[t.key][t.index][t.col]
      return Array.isArray(cell) ? cell : []
    },
    nestedCols() {
      const arr = this.nestedRows
      const zh = { label: '指标名', unit: '单位', value: '数值', min: '最小值', max: '最大值', digits: '小数位', name: '名称', key: '标识', desc: '说明' }
      return this.columnsOf(arr).map(k => ({ key: k, label: zh[k] || k }))
    },
    nestedTitle() {
      const t = this.nestedTarget
      if (!t || !this.model) return '逐项编辑'
      const row = this.model[t.key] && this.model[t.key][t.index]
      const nm = row && (row.name || row.label || '')
      return (nm ? nm + ' · ' : '') + '运行指标逐项编辑'
    },
  },
  watch: { scope() { this.reload() } },
  created() { this.reload() },
  methods: {
    async reload() {
      let content = null
      try { content = await api.fetchScope(this.scope) } catch (e) { content = null }
      const remoteFields = (content && content.fields) || {}
      const remoteAssets = (content && content.assets) || {}
      const remoteTexts = (content && content.texts) || {}
      store.replace(this.scope, { texts: remoteTexts, fields: remoteFields, assets: remoteAssets })
      if (this.fieldModel) this.initModel(remoteFields)
      else this.buildRows(remoteTexts)
      Vue.set(state.dirty, this.scope, false)
    },
    /* ---------- 新模型 ---------- */
    initModel(remoteFields) {
      const m = {}
      for (const g of this.fieldModel.groups) {
        for (const f of g.fields) {
          const def = f.default
          const remote = remoteFields[f.key]
          // 远端有值用远端（可能覆盖默认值），否则用默认值；保证后台展示的是「页面当前值」
          m[f.key] = (remote !== undefined && remote !== null && remote !== '')
            ? this.clone(remote)
            : this.clone(def)
        }
      }
      this.model = m
    },
    clone(v) { return JSON.parse(JSON.stringify(v)) },
    touch() { Vue.set(state.dirty, this.scope, true) },
    setField(key, value) { this.$set(this.model, key, value); this.touch() },
    jsonOf(key) {
      const v = this.model[key]
      return (v === undefined || v === null) ? '' : JSON.stringify(v, null, 2)
    },
    setJson(key, v) {
      try { this.setField(key, JSON.parse(v)); } catch (e) { /* 允许暂存非法 JSON */ }
    },
    isObjArray(v) { return Array.isArray(v) && v.length > 0 && v[0] && typeof v[0] === 'object' },
    columnsOf(arr) {
      const s = []
      arr.forEach(r => Object.keys(r).forEach(k => { if (s.indexOf(k) < 0) s.push(k) }))
      return s
    },
    addRow(key) {
      const arr = this.model[key]
      if (!Array.isArray(arr)) return
      const row = {}
      this.columnsOf(arr).forEach(c => {
        // 嵌套对象/数组列（如 metrics）不在新增行里塞字符串，避免污染前端数据结构
        const sample = arr.find(r => r && typeof r === 'object' && r[c] !== null && typeof r[c] === 'object')
        if (sample) return
        row[c] = ''
      })
      arr.push(row); this.touch(); this.$forceUpdate()
    },
    delRow(key, idx) { this.model[key].splice(idx, 1); this.touch(); this.$forceUpdate() },
    /* ---------- 媒体（新模型） ---------- */
    mediaUrl(v) { return v ? mediaUrl(v) : '' },
    /* 判断表格列是否为媒体列（可上传/选素材） */
    isMediaCol(col) {
      return /^(video|videos|image|images|img|pic|poster|cover|photo|logo|icon|bg|background|media)$/i.test(String(col))
        || /视频|图片|图像|封面/.test(String(col))
    },
    colAccept(col) { return /video|视频/i.test(String(col)) ? 'video' : 'image' },
    /* 打开素材库弹窗（依赖 currentTarget 已设置） */
    openLibrary() {
      const accept = this.currentTarget && this.currentTarget.accept
      api.mediaList(accept).then(list => {
        this.mediaItems = list.map(m => Object.assign({}, m, { url: mediaUrl(m.url) }))
        this.libVisible = true
      }).catch(() => { this.mediaItems = []; this.libVisible = true })
    },
    pickFromLibrary(key, accept) {
      this.currentTarget = { kind: 'field', key, accept }
      this.fileAccept = accept === 'video' ? 'video/*' : 'image/*'
      this.openLibrary()
    },
    pickFromLibraryCell(key, index, col) {
      const accept = this.colAccept(col)
      this.currentTarget = { kind: 'cell', key, index, col, accept }
      this.fileAccept = accept === 'video' ? 'video/*' : 'image/*'
      this.openLibrary()
    },
    applyTarget(url) {
      const t = this.currentTarget
      if (!t) return
      if (t.kind === 'field') this.setField(t.key, url)
      else this.setCell(t.key, t.index, t.col, url)
    },
    chooseMedia(m) {
      this.applyTarget(m.url)
      this.libVisible = false
      this.$message.success('已应用素材')
    },
    pickLocal(key, accept) {
      this.currentTarget = { kind: 'field', key, accept }
      this.fileAccept = accept === 'video' ? 'video/*' : 'image/*'
      this.$refs.file.click()
    },
    pickLocalCell(key, index, col) {
      const accept = this.colAccept(col)
      this.currentTarget = { kind: 'cell', key, index, col, accept }
      this.fileAccept = accept === 'video' ? 'video/*' : 'image/*'
      this.$refs.file.click()
    },
    setCell(key, index, col, value) {
      const arr = this.model[key]
      if (!Array.isArray(arr) || !arr[index]) return
      this.$set(arr[index], col, value)
      this.touch()
    },
    /* ---------- 嵌套 JSON 单元格 & 数字保持 ---------- */
    /* 宽内容字段：跨满整行（数组表格 / 对象 JSON / 多行文本） */
    isWideField(f) {
      return ['array', 'object', 'textarea'].indexOf(f.type) >= 0
    },
    colLabel(col, arr) {
      const hasObj = Array.isArray(arr) && arr.some(r => r && typeof r === 'object' && r[col] !== null && typeof r[col] === 'object')
      return hasObj ? col + '（逐项编辑）' : col
    },
    isJsonCell(row, col) {
      const v = row && row[col]
      return v !== null && typeof v === 'object'
    },
    /* 嵌套「对象数组」单元格：走逐项数据框编辑 */
    isNestedArrCell(row, col) {
      const v = row && row[col]
      return Array.isArray(v) && v.length > 0 && v[0] && typeof v[0] === 'object'
    },
    nestedSummary(row, col) {
      const v = row && row[col]
      if (!Array.isArray(v)) return '空'
      if (!v.length) return '0 项'
      return v.map(m => (m && (m.label || m.name || m.key)) || '?').join('、')
    },
    openNested(key, index, col) {
      this.nestedTarget = { key, index, col }
      this.nestedVisible = true
    },
    setNestedCell(rowIndex, colKey, v) {
      const t = this.nestedTarget
      const row = this.nestedRows[rowIndex]
      if (!t || !row) return
      // 原值为数字：输入可解析则存回数字（value/min/max/digits 供 toFixed 与波动逻辑使用）
      if (typeof row[colKey] === 'number' && v !== '' && !isNaN(Number(v))) v = Number(v)
      this.$set(row, colKey, v)
      this.touch()
    },
    addNestedRow() {
      const t = this.nestedTarget
      if (!t) return
      const arr = this.nestedRows
      const row = {}
      this.nestedCols.forEach(c => {
        const sample = arr.find(r => r && typeof r === 'object')
        row[c.key] = sample && sample[c.key] !== null && typeof sample[c.key] === 'number' ? 0 : ''
      })
      arr.push(row); this.touch(); this.$forceUpdate()
    },
    delNestedRow(rowIndex) { this.nestedRows.splice(rowIndex, 1); this.touch(); this.$forceUpdate() },
    cellStr(row, col) {
      const v = row ? row[col] : undefined
      return v === undefined || v === null ? '' : String(v)
    },
    setCellSmart(key, index, col, v) {
      const arr = this.model[key]
      const orig = arr && arr[index] ? arr[index][col] : undefined
      // 原值是数字：输入能解析则存回数字（如 metrics.value 供 toFixed 使用），清空则存空串
      if (typeof orig === 'number' && v !== '' && !isNaN(Number(v))) v = Number(v)
      this.setCell(key, index, col, v)
    },
    cellJson(key, index, col) {
      try { return JSON.stringify(this.model[key][index][col], null, 2) } catch (e) { return '' }
    },
    setCellJson(key, index, col, v) {
      try {
        const parsed = JSON.parse(v)
        this.setCell(key, index, col, parsed)
      } catch (e) { /* 允许暂存非法 JSON，解析合法后再落值 */ }
    },
    onFilePicked(e) {
      const f = e.target.files && e.target.files[0]
      if (!f) return
      const loading = this.$loading({ text: '上传中', background: 'rgba(0,0,0,.5)' })
      api.uploadMedia(f)
        .then(m => { this.applyTarget(m.url); this.$message.success('已上传并应用') })
        .catch(err => this.$message.error(err.message || '上传失败'))
        .then(() => { loading.close(); e.target.value = '' })
    },
    /* ---------- 旧模型兼容 ---------- */
    buildRows(remoteTexts) {
      const discovered = (state.discovered && state.discovered[this.scope]) || {}
      const keys = []
      const seen = {}
      for (const k of Object.keys(discovered)) if (!seen[k]) { seen[k] = 1; keys.push(k) }
      for (const k of Object.keys(remoteTexts)) if (!seen[k]) { seen[k] = 1; keys.push(k) }
      this.rows = keys.map(k => ({ key: k, value: remoteTexts[k] === undefined || remoteTexts[k] === k ? '' : remoteTexts[k] }))
      this.page = 1
    },
    valueOfAsset(key) {
      const a = this.bundle && this.bundle.assets
      return a ? mediaUrl(a[key] || '') : ''
    },
    pickFromLibraryOld(a) {
      this.currentAnchor = a
      this.fileAccept = a.accept === 'video' ? 'video/*' : 'image/*'
      api.mediaList(a.accept).then(list => {
        this.mediaItems = list.map(m => Object.assign({}, m, { url: mediaUrl(m.url) }))
        this.libVisible = true
      }).catch(() => { this.mediaItems = []; this.libVisible = true })
    },
    chooseMediaOld(m) { this.setAsset(this.currentAnchor.key, m.url); this.libVisible = false; this.$message.success('已应用素材') },
    pickLocalOld(a) {
      this.currentAnchor = a
      this.fileAccept = a.accept === 'video' ? 'video/*' : 'image/*'
      this.$refs.file.click()
    },
    onFilePickedOld(e) {
      const f = e.target.files && e.target.files[0]
      if (!f) return
      const loading = this.$loading({ text: '上传中', background: 'rgba(0,0,0,.5)' })
      api.uploadMedia(f)
        .then(m => { this.setAsset(this.currentAnchor.key, m.url); this.$message.success('已上传并应用') })
        .catch(err => this.$message.error(err.message || '上传失败'))
        .then(() => { loading.close(); e.target.value = '' })
    },
    setAsset(key, raw) { store.set(this.scope, 'assets', key, raw); Vue.set(state.dirty, this.scope, true) },
    clearAsset(a) { this.setAsset(a.key, '') },
    addRowOld() { this.rows.unshift({ key: '', value: '' }); this.page = 1; Vue.set(state.dirty, this.scope, true) },
    removeRow(idx) { this.rows.splice(idx, 1); Vue.set(state.dirty, this.scope, true) },
    dataOf(k) { return this.dataObj[k] },
    jsonOfOld(k) { return JSON.stringify(this.dataOf(k), null, 2) },
    setJsonOld(k, v) { try { store.set(this.scope, 'fields', k, JSON.parse(v)); Vue.set(state.dirty, this.scope, true) } catch (e) {} },
    typeLabel(v) {
      if (Array.isArray(v)) return `数组 · ${v.length} 项`
      if (v && typeof v === 'object') return '对象'
      return typeof v === 'number' ? '数字' : '文本'
    },
    addDataRow(k) {
      const arr = this.dataOf(k); const row = {}
      this.columnsOf(arr).forEach(c => { row[c] = '' })
      arr.push(row); Vue.set(state.dirty, this.scope, true); this.$forceUpdate()
    },
    delDataRow(k, idx) { this.dataOf(k).splice(idx, 1); Vue.set(state.dirty, this.scope, true); this.$forceUpdate() },
    /* ---------- 保存 ---------- */
    async save() {
      this.saving = true
      try {
        if (this.fieldModel) {
          const existing = this.bundle || {}
          store.replace(this.scope, {
            texts: existing.texts || {},
            fields: this.clone(this.model),
            assets: existing.assets || {},
          })
        } else {
          // 旧模型：把 rows 写回 texts
          const texts = {}
          for (const r of this.rows) {
            if (!r.key) continue
            if (!r.value || r.value === r.key) continue
            texts[r.key] = r.value
          }
          const b = this.bundle || {}
          store.replace(this.scope, { texts, fields: b.fields || {}, assets: b.assets || {} })
        }
        await save(this.scope)
        await load()
        await this.reload()
        this.$message.success('已保存，大屏刷新后生效')
      } catch (e) {
        this.$message.error(e.message || '保存失败')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="less" scoped>
.pp { color: #d8e8ff; }
.pp-head { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 18px; }
.pp-title { margin: 0 0 4px; font-size: 16px; color: #eaf3ff; }
.pp-scope { font-size: 11px; color: #6f8bb5; }
.pp-ops { display: flex; gap: 10px; }
.pp-block { margin-bottom: 26px; }
.pp-h3 { font-size: 13px; color: #7fe3ff; margin: 0 0 10px; font-weight: 600; display: flex; align-items: center; gap: 12px; }
.pp-h3-sub { font-size: 11px; color: #6f8bb5; font-weight: 400; }

/* 新模型 */
.pp-fields { display: grid; grid-template-columns: repeat(auto-fill, minmax(420px, 1fr)); gap: 16px 28px; }
.pp-field { display: flex; flex-direction: column; gap: 8px; }
/* 数组表格/对象 JSON/多行文本等宽内容字段：跨满整行，表格不挤压
   注意：Less 会把 grid-column: 1 / -1 中的斜杠当除法，必须用长写属性 */
.pp-field-wide {
  grid-column-start: 1;
  grid-column-end: -1;
}
.pp-field-label { font-size: 11px; color: #9fc0e8; }
.pp-media { display: flex; gap: 12px; align-items: center; }
.pp-media-prev { width: 150px; height: 88px; flex: none; border-radius: 8px; overflow: hidden; background: #08152c; display: flex; align-items: center; justify-content: center; }
.pp-media-prev img, .pp-media-prev video { width: 100%; height: 100%; object-fit: cover; }
.pp-media-empty { font-size: 11px; color: #6f8bb5; text-align: center; }
.pp-media-btns { display: flex; gap: 8px; flex-wrap: wrap; }
.pp-cell-media { display: flex; flex-direction: column; gap: 4px; }
.pp-cell-btns { display: flex; gap: 4px; flex-wrap: wrap; line-height: 1; }
.pp-cell-btns .el-button { padding: 2px 4px; }
.pp-cell-clear { color: #ff7a6b !important; }
/* 嵌套对象数组单元格：摘要 + 逐项编辑按钮 */
.pp-cell-nested { display: flex; flex-direction: column; gap: 6px; }
.pp-cell-nested-sum {
  font-size: 11px; line-height: 1.5; color: #bcd3f5; word-break: break-all;
  max-height: 66px; overflow: hidden;
}
.pp-nested-tip { margin: 10px 0 0; font-size: 11px; color: #6f8bb5; }

/* 旧模型 */
.pp-assets { display: grid; grid-template-columns: repeat(auto-fill, minmax(430px, 1fr)); gap: 14px; }
.pp-asset { display: flex; gap: 12px; padding: 12px; border: 1px solid rgba(80, 150, 240, .22); border-radius: 10px; background: rgba(13, 32, 64, .5); }
.pp-asset-preview { width: 150px; height: 88px; flex: none; border-radius: 8px; overflow: hidden; background: #08152c; display: flex; align-items: center; justify-content: center; }
.pp-asset-preview img, .pp-asset-preview video { width: 100%; height: 100%; object-fit: cover; }
.pp-asset-empty { font-size: 11px; color: #6f8bb5; text-align: center; line-height: 1.6; }
.pp-asset-label { margin: 0 0 4px; font-size: 13px; color: #eaf3ff; }
.pp-asset-hint { margin: 0 0 10px; font-size: 11px; color: #6f8bb5; }
.pp-asset-btns { display: flex; gap: 8px; flex-wrap: wrap; }
.pp-search { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.pp-tip { font-size: 11px; color: #6f8bb5; }
.pp-table { background: transparent; }
.pp-key { color: #eaf3ff; font-size: 13px; }
.pp-key-type { margin-left: 10px; font-size: 11px; color: #ffc857; }
.pp-data-empty { padding: 18px; border: 1px dashed rgba(80, 150, 240, .25); border-radius: 8px; color: #6f8bb5; font-size: 12px; }
.pp-lib { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px; max-height: 60vh; overflow: auto; }
.pp-lib-item { border: 1px solid rgba(80, 150, 240, .22); border-radius: 8px; overflow: hidden; cursor: pointer; background: rgba(13, 32, 64, .5); transition: border-color .2s; }
.pp-lib-item:hover { border-color: #31ffff; }
.pp-lib-item img, .pp-lib-item video { width: 100%; height: 96px; object-fit: cover; display: block; background: #08152c; }
.pp-lib-file { display: block; height: 96px; line-height: 96px; text-align: center; font-size: 11px; color: #9fc0e8; }
.pp-lib-name { margin: 0; padding: 6px; font-size: 11px; color: #9fc0e8; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pp-lib-empty { grid-column: 1 / -1; text-align: center; color: #6f8bb5; padding: 30px 0; }
</style>
